import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './routes/MyContextProvider';
import './LogIn_LogOut.css';

const RTL_LANGUAGE_PREFIXES = ['ar', 'fa', 'he', 'ur'];

const LogIn_LogOut = () => {
  const { t, i18n } = useTranslation();
  const {
    isLoggedIn,
    user,
    login,
    logout,
    resetPassword,
    startSsoLogin,
    refreshSession,
    authLoading
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const languageCode = (i18n?.language || 'en').toLowerCase();
  const isRtl = RTL_LANGUAGE_PREFIXES.some((prefix) => languageCode.startsWith(prefix));
  const pageDirection = isRtl ? 'rtl' : 'ltr';

  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [resetEmail, setResetEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

const ssoProviders = useMemo(
  () => [
    {
      key: 'google',
      label: t('Continue with Google'),
      icon: 'G',
      disabled: false
    },
    {
      key: 'apple',
      label: t('Continue with Apple'),
      icon: '',
      disabled: true
    },
    {
      key: 'microsoft',
      label: t('Continue with Microsoft'),
      icon: 'M',
      disabled: true
    }
  ],
  [t]
);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('auth') === 'success') {
      refreshSession()
        .then(() => setMessage(t('Successfully logged in')))
        .catch(() => setMessage(t('We could not confirm your sign-in session. Please try again.')))
        .finally(() => navigate('/login_logout', { replace: true }));
    }
  }, [location.search, navigate, refreshSession, t]);

  const clearStateForTab = (tabName) => {
    setActiveTab(tabName);
    setErrors({});
    setMessage('');
  };

  const handleCredentialChange = (event) => {
    const { name, value } = event.target;
    setCredentials((previous) => ({ ...previous, [name]: value }));
  };

  const validateLogin = () => {
    const nextErrors = {};

    if (!credentials.username.trim()) {
      nextErrors.username = t('Username or email is required');
    }

    if (!credentials.password) {
      nextErrors.password = t('Password is required');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateReset = () => {
    const nextErrors = {};

    if (!resetEmail.trim()) {
      nextErrors.resetEmail = t('Email is required');
    } else if (!/^\S+@\S+\.\S+$/.test(resetEmail.trim())) {
      nextErrors.resetEmail = t('Invalid email format');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    if (!validateLogin()) return;

    setIsSubmitting(true);
    try {
      await login(credentials.username.trim(), credentials.password);
      setMessage(t('Successfully logged in'));
      const destination = location.state?.from?.pathname || '/';
      navigate(destination, { replace: true });
    } catch (error) {
      setMessage(error?.message || t('Login failed. Please check your username and password.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    if (!validateReset()) return;

    setIsSubmitting(true);
    try {
      await resetPassword(resetEmail.trim());
      setMessage(t('Password reset instructions were sent if this account exists.'));
    } catch (error) {
      setMessage(error?.message || t('Password reset failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setIsSubmitting(true);
    try {
      await logout();
      setMessage(t('Successfully logged out'));
    } catch (error) {
      setMessage(error?.message || t('Logout failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSsoClick = (providerKey) => {
    setMessage('');
    startSsoLogin(providerKey);
  };

  if (authLoading) {
    return (
      <main className="auth-page" dir={pageDirection} lang={languageCode}>
        <section className="auth-card" aria-live="polite">
          <p className="auth-message neutral">{t('Checking your session...')}</p>
        </section>
      </main>
    );
  }

  if (isLoggedIn) {
    return (
      <main className="auth-page" dir={pageDirection} lang={languageCode}>
        <section className="auth-card">
          <div className="auth-header">
            <p className="auth-eyebrow">{t('Stark RWAE Exchange')}</p>
            <h1>{t('Logout')}</h1>
            <p>{t('You are currently signed in.')}</p>
          </div>

          {user?.displayName || user?.email || user?.username ? (
            <div className="signed-in-box">
              <span className="signed-in-label">{t('Signed in as')}</span>
              <strong>{user.displayName || user.email || user.username}</strong>
            </div>
          ) : null}

          <button
            type="button"
            className="auth-button logout"
            onClick={handleLogout}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('Please wait...') : t('Logout')}
          </button>

          {message ? <p className="auth-message">{message}</p> : null}
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page" dir={pageDirection} lang={languageCode}>
      <section className="auth-card">
        <div className="auth-header">
          <p className="auth-eyebrow">{t('Stark RWAE Exchange')}</p>
          <h1>{activeTab === 'login' ? t('Login') : t('Reset Password')}</h1>
          <p>
            {activeTab === 'login'
              ? t('Sign in with an approved username and password or use single sign-on.')
              : t('Enter your email address and we will send password reset instructions if the account exists.')}
          </p>
        </div>

        <div className="auth-tabs" role="tablist" aria-label={t('Authentication options')}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'login'}
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => clearStateForTab('login')}
          >
            {t('Login')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'reset'}
            className={`auth-tab ${activeTab === 'reset' ? 'active' : ''}`}
            onClick={() => clearStateForTab('reset')}
          >
            {t('Reset Password')}
          </button>
        </div>

        {activeTab === 'login' ? (
          <form onSubmit={handleLoginSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="username">{t('Username or email')}</label>
              <input
                type="text"
                id="username"
                name="username"
                value={credentials.username}
                onChange={handleCredentialChange}
                className={errors.username ? 'error' : ''}
                placeholder={t('Enter your username or email')}
                autoComplete="username"
              />
              {errors.username ? <span className="error-message">{errors.username}</span> : null}
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('Password')}</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleCredentialChange}
                  className={errors.password ? 'error' : ''}
                  placeholder={t('Enter your password')}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? t('Hide password') : t('Show password')}
                >
                  {showPassword ? t('Hide') : t('Show')}
                </button>
              </div>
              {errors.password ? <span className="error-message">{errors.password}</span> : null}
            </div>

            <button type="submit" className="auth-button" disabled={isSubmitting}>
              {isSubmitting ? t('Please wait...') : t('Login')}
            </button>

            <p className="auth-link">
              <Link to="#" onClick={() => clearStateForTab('reset')}>
                {t('Forgot your password?')}
              </Link>
            </p>

            <div className="auth-divider" aria-hidden="true">
              <span>{t('or')}</span>
            </div>

            <div className="sso-buttons" aria-label={t('Single sign-on options')}>
              {ssoProviders.map((provider) => (
<button
  key={provider.key}
  type="button"
  className={`sso-button ${provider.key}`}
  onClick={() => handleSsoClick(provider.key)}
  disabled={provider.disabled}
>
                  <span aria-hidden="true" className="sso-icon">{provider.icon}</span>
                  <span>{provider.label}</span>
                </button>
              ))}
            </div>

            <p className="auth-note">
              {t('New access requests are handled through the Pricing & Access Request page.')}
            </p>
          </form>
        ) : (
          <form onSubmit={handleResetSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="resetEmail">{t('Email')}</label>
              <input
                type="email"
                id="resetEmail"
                name="resetEmail"
                value={resetEmail}
                onChange={(event) => setResetEmail(event.target.value)}
                className={errors.resetEmail ? 'error' : ''}
                placeholder={t('Enter your email')}
                autoComplete="email"
              />
              {errors.resetEmail ? <span className="error-message">{errors.resetEmail}</span> : null}
            </div>

            <button type="submit" className="auth-button" disabled={isSubmitting}>
              {isSubmitting ? t('Please wait...') : t('Send Reset Link')}
            </button>

            <p className="auth-link">
              <Link to="#" onClick={() => clearStateForTab('login')}>
                {t('Back to login')}
              </Link>
            </p>
          </form>
        )}

        {message ? <p className="auth-message">{message}</p> : null}
      </section>
    </main>
  );
};

export default LogIn_LogOut;
